import { useState } from "react";
import { formatIDR, fmtDate } from "@/lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// Status invoice kanonis (backend: compute_invoice_origin_status). Frontend TIDAK menghitung ulang.
const OFF_TONE = {
  paid: "bg-emerald-50 text-emerald-800 border-emerald-300",
  partially_paid: "bg-amber-50 text-amber-800 border-amber-300",
  verified_unpaid: "bg-rose-50 text-rose-800 border-rose-300",
  draft: "bg-slate-50 text-slate-600 border-slate-300",
  none: "bg-slate-50 text-slate-600 border-slate-300",
};
const ORIGIN = { official: "Invoice resmi", legacy: "Catatan legacy", mixed: "Invoice resmi + catatan legacy", none: "Tidak ada invoice resmi maupun legacy" };
const pill = "inline-block px-1.5 py-0.5 text-[10px] font-semibold border leading-tight";

const Line = ({ label, value, testid }) => (
  <div className="flex justify-between gap-3 py-0.5 border-b border-slate-50 text-xs" data-testid={testid}>
    <span className="text-slate-500">{label}</span><span className="font-medium text-right tabular-nums">{value}</span>
  </div>
);

export const InvoiceStatusDetail = ({ s }) => (
  <div className="space-y-3" data-testid="inv-status-detail">
    <div>
      <Line label="Asal data" value={ORIGIN[s.invoice_origin]} testid="inv-status-origin" />
      <Line label="Jenis invoice" value={`${s.official_invoice_count} resmi · ${s.legacy_record_count} dengan catatan legacy · ${s.draft_invoice_count} draft`} />
      <Line label="Status invoice resmi" value={s.official_label} />
      <Line label="Nominal invoice resmi (Kas Harus Diterima)" value={formatIDR(s.official_invoice_total)} testid="inv-status-official-total" />
      <Line label="Penerimaan resmi teralokasi" value={formatIDR(s.official_received_total)} testid="inv-status-official-received" />
      <Line label="Status catatan legacy" value={s.legacy_label || "—"} />
      <Line label="Nominal legacy" value={formatIDR(s.legacy_invoice_total)} testid="inv-status-legacy-total" />
      <Line label="Penerimaan legacy" value={formatIDR(s.legacy_received_total)} testid="inv-status-legacy-received" />
      <Line label="Penerimaan belum diklasifikasikan" value={formatIDR(s.unclassified_receipt_total)} testid="inv-status-unclassified" />
      <Line label="Pembaruan terakhir" value={s.updated_at ? fmtDate(s.updated_at) : "—"} />
    </div>
    <div>
      <div className="text-[10px] uppercase text-slate-500 mb-1">Alasan status</div>
      <ul className="text-xs space-y-1">{s.reasons.map((r) => (
        <li key={r.code} className="border-l-2 border-slate-300 pl-2">{r.text}<div className="text-[10px] text-slate-400">Sumber: {r.source}</div></li>
      ))}</ul>
    </div>
    <p className="text-[11px] text-slate-500">Status legacy berasal dari saldo historis dan bukan bukti adanya invoice resmi terverifikasi. Penerimaan belum diklasifikasikan tidak menaikkan progres pembayaran resmi.</p>
  </div>
);

/** Badge status invoice kanonis + drill-down. variant "pill" (ringkas) | "blocks" (3 blok terpisah). */
export default function InvoiceOriginStatus({ status, variant = "pill", testid = "inv-status" }) {
  const [open, setOpen] = useState(false);
  if (!status) return <span className="text-[10px] text-slate-400">—</span>;
  const s = status;
  const body = variant === "blocks" ? (
    <div className="grid gap-1 text-[10px] text-left">
      <div><span className="text-slate-500 uppercase">Invoice Resmi: </span><span className={`${pill} ${OFF_TONE[s.official_invoice_status]}`}>{s.official_label}</span></div>
      <div><span className="text-slate-500 uppercase">Catatan Legacy: </span>{s.legacy_label ? <span className={`${pill} bg-slate-50 text-slate-700 border-slate-300`}>{s.legacy_label}</span> : <span className="text-slate-400">tidak ada</span>}</div>
      <div><span className="text-slate-500 uppercase">Penerimaan Belum Diklasifikasikan: </span><span className="tabular-nums">{formatIDR(s.unclassified_receipt_total)}</span>
        {s.reconciliation_badge && <span className={`${pill} ml-1 bg-amber-50 text-amber-800 border-amber-300`}>{s.reconciliation_badge}</span>}</div>
    </div>
  ) : (
    <div className="flex flex-col items-start gap-0.5">
      <span className={`${pill} ${s.official_invoice_count ? OFF_TONE[s.official_invoice_status] : "bg-slate-50 text-slate-600 border-slate-300"}`}>{s.official_label}</span>
      {s.legacy_label && <span className={`${pill} bg-slate-50 text-slate-700 border-slate-300`} title="Saldo historis — bukan bukti invoice resmi terverifikasi">{s.legacy_label}</span>}
      {s.reconciliation_badge && <span className={`${pill} bg-amber-50 text-amber-800 border-amber-300`}>Perlu Rekonsiliasi</span>}
    </div>
  );
  return (
    <>
      <button type="button" onClick={(e) => { e.stopPropagation(); setOpen(true); }} className="text-left hover:opacity-80" data-testid={testid} title="Klik untuk rincian status">{body}</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-none" data-testid={`${testid}-dialog`}>
          <DialogHeader><DialogTitle>Rincian Status Invoice</DialogTitle></DialogHeader>
          <InvoiceStatusDetail s={s} />
        </DialogContent>
      </Dialog>
    </>
  );
}
